import { useAuth } from "src/contexts/auth-provider";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GET_RECEIPT, REFUND_CUSTOMER_ORDER_PAYMENT } from "src/constants/queries";
import { failureToast, successToast } from "src/utils/toasts";
import { convertCentsToDollars, convertDollarToCents } from "src/helpers";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import {
  OrderSourceEnum,
  OrderStatusEnum,
  OrderTypeEnum,
  OrderVoidReasonCode,
  PaymentStatusEnum,
} from "src/enums/order.enum";
import { CustomerOrder, OrderDiscount, UpdateCustomerOrderInput } from "src/interfaces/order.interface";
import {
  formatOrderDiscountRuleDisplay,
  formatOrderDiscountValueDisplay,
} from "src/helpers/orderDiscountDisplay";
import {
  getOrderStatusStyle,
  getPaymentStatusStyle,
  ORDER_BADGE_CLASS,
  PAYMENT_BADGE_CLASS,
  BADGE_PADDING_STYLE,
} from "src/constants/orderStatusBadges";
import {
  orderDrawerCompactSecondaryButtonClass,
  orderDrawerPrimaryButtonClass,
  orderDrawerSecondaryButtonClass,
} from "src/constants/orderDrawerButtons";
import CustomerDetails from "src/features/Orders/components/CustomerDetails";
import PaymentDetails from "src/features/Orders/components/PaymentDetails";
import { OrderStatusMenuSelect } from "src/components/common/OrderStatusMenuSelect";
import { formatOrderVoidReasonCode, formatVoidedAt } from "src/helpers/orderVoidDisplay";
import { ConfirmOrderStatusChangeModal } from "src/components/common/ConfirmOrderStatusChangeModal";
import { VoidOrderModal } from "src/components/common/VoidOrderModal";
import { ConfirmActionModal } from "src/components/common/ConfirmActionModal";
import { resolveVoidedByDisplay } from "src/helpers/resolveVoidedByDisplay";

/** Matches active-order drawer: narrow shared field width, room for Update Tip without horizontal scroll. */
const ORDER_DRAWER_CONTROL_WIDTH = "w-32 shrink-0";

export interface CompletedOrderDetailsPanelProps {
  customerOrder: CustomerOrder;
  onClose: () => void;
  handleUpdateOrder: (variables: UpdateCustomerOrderInput) => Promise<boolean> | Promise<void> | void;
  isUpdatingCustomerOrder: boolean;
  onPrint?: () => void;
  /** Optional prev/next navigation */
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

export default function CompletedOrderDetailsPanel({
  customerOrder,
  onClose,
  handleUpdateOrder,
  isUpdatingCustomerOrder,
  onPrint,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
}: CompletedOrderDetailsPanelProps) {
  const { hasRestaurant, user } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const [isDownloading, setIsDownloading] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<OrderStatusEnum | null>(null);
  const [statusConfirmLoading, setStatusConfirmLoading] = useState(false);
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [voidSubmitting, setVoidSubmitting] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundSubmitting, setRefundSubmitting] = useState(false);

  const formik = useFormik<{ tip: string | undefined }>({
    initialValues: { tip: undefined },
    validationSchema: Yup.object().shape({
      tip: Yup.string(),
    }),
    onSubmit: () => {},
  });

  useEffect(() => {
    const tipDisplay =
      customerOrder?.tipCents != null
        ? String(convertCentsToDollars(customerOrder.tipCents))
        : "0.00";
    formik.setFieldValue("tip", tipDisplay);
  }, [customerOrder?.correlationId, customerOrder?.tipCents]);

  useEffect(() => {
    setPendingStatusChange(null);
    setVoidModalOpen(false);
    setRefundModalOpen(false);
  }, [customerOrder?.correlationId]);

  const handleStatusChange = (orderStatus: OrderStatusEnum) => {
    if (orderStatus === customerOrder.orderStatus) return;
    if (orderStatus === OrderStatusEnum.VOIDED) {
      setVoidModalOpen(true);
      return;
    }
    setPendingStatusChange(orderStatus);
  };

  const handleCancelStatusChange = () => {
    if (statusConfirmLoading) return;
    setPendingStatusChange(null);
  };

  const handleConfirmStatusChange = async () => {
    if (pendingStatusChange == null) return;
    setStatusConfirmLoading(true);
    try {
      const res = await Promise.resolve(
        handleUpdateOrder({
          storeCorrelationId: hasRestaurant?.correlationId!,
          customerOrderCorrelationId: customerOrder.correlationId,
          orderStatus: pendingStatusChange,
          orderSource: OrderSourceEnum.RESTAURANT_WEB_APP,
        })
      );
      if (res !== false) {
        setPendingStatusChange(null);
        onClose();
      }
    } finally {
      setStatusConfirmLoading(false);
    }
  };

  const handleCancelVoid = () => {
    if (voidSubmitting) return;
    setVoidModalOpen(false);
  };

  const handleConfirmVoid = async (voidReasonCode: OrderVoidReasonCode, voidReasonNote: string | null) => {
    const voidedBy = resolveVoidedByDisplay(user);
    if (voidedBy == null || voidedBy.trim() === "") {
      failureToast("Could not determine who is voiding this order. Try logging in again.");
      return;
    }
    setVoidSubmitting(true);
    try {
      const res = await Promise.resolve(
        handleUpdateOrder({
          storeCorrelationId: hasRestaurant?.correlationId!,
          customerOrderCorrelationId: customerOrder.correlationId,
          orderStatus: OrderStatusEnum.VOIDED,
          voidReasonCode,
          voidReasonNote: voidReasonNote ?? undefined,
          voidedBy,
          orderSource: OrderSourceEnum.RESTAURANT_WEB_APP,
        })
      );
      if (res === false) {
        failureToast("Could not void this order. Please try again.");
        return;
      }
      successToast("This order has been voided.");
      setVoidModalOpen(false);
      onClose();
    } catch {
      failureToast("Could not void this order. Please try again.");
    } finally {
      setVoidSubmitting(false);
    }
  };

  const handleUpdateTip = () => {
    const tipText = String(formik.values.tip ?? "").trim();
    if (tipText === "") {
      failureToast("Please enter a tip amount.");
      return;
    }
    const tipAmount = Number(tipText);
    if (Number.isNaN(tipAmount) || tipAmount < 0) {
      failureToast("Please enter a valid tip amount.");
      return;
    }
    handleUpdateOrder({
      storeCorrelationId: hasRestaurant?.correlationId!,
      customerOrderCorrelationId: customerOrder.correlationId,
      orderStatus: customerOrder.orderStatus,
      tipCents: convertDollarToCents(tipAmount),
      orderSource: OrderSourceEnum.RESTAURANT_WEB_APP,
    });
  };

  const downloadReceipt = async () => {
    try {
      setIsDownloading(true);
      const { data: orderReceipt } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_RECEIPT,
        variables: {
          getReceiptPreSignedUrlInput: {
            customerOrderCorrelationId: customerOrder?.correlationId,
            storeCorrelationId: hasRestaurant?.correlationId,
          },
        },
      });
      const url = orderReceipt?.data?.receiptByCustomerOrderCorrelationIdForStores?.customerCopy;
      if (url) {
        const res = await fetch(url);
        const blob = await res.blob();
        const fileURL = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = fileURL;
        a.download = `${customerOrder?.correlationId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(fileURL);
        successToast("Receipt downloaded.");
      } else {
        throw new Error(orderReceipt?.error || "Failed to get receipt.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCancelRefund = () => {
    if (refundSubmitting) return;
    setRefundModalOpen(false);
  };

  const refundOrderPayment = async () => {
    if (!hasRestaurant?.correlationId || !customerOrder?.correlationId) return;

    setRefundSubmitting(true);
    try {
      const { data } = await axBe.post<{ errors?: Array<{ message?: string }> }>(GRAPHQL_ENDPOINT, {
        query: REFUND_CUSTOMER_ORDER_PAYMENT,
        variables: {
          refundCustomerOrderPaymentInput: {
            storeCorrelationId: hasRestaurant.correlationId,
            customerOrderCorrelationId: customerOrder.correlationId,
          },
        },
      });
      const graphQLError = data?.errors?.[0]?.message;
      if (graphQLError) throw new Error(graphQLError);
      successToast("Refund request submitted. Status will update after processor confirmation.");
      setRefundModalOpen(false);
      onClose();
    } catch (error: any) {
      failureToast(error?.message || "Refund is not available for this order yet.");
    } finally {
      setRefundSubmitting(false);
    }
  };

  const viewSignature = async () => {
    if (!customerOrder?.signatureBucketKeyName) return;
    try {
      setSignatureLoading(true);
      const { data: orderReceipt } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_RECEIPT,
        variables: {
          getReceiptPreSignedUrlInput: {
            customerOrderCorrelationId: customerOrder?.correlationId,
            storeCorrelationId: hasRestaurant?.correlationId,
          },
        },
      });
      const url = orderReceipt?.data?.receiptByCustomerOrderCorrelationIdForStores?.signatureImage;
      if (!url) throw new Error("Signature is not available.");
      setSignatureUrl(url);
    } catch (err: any) {
      failureToast(err?.message || "Failed to load signature.");
    } finally {
      setSignatureLoading(false);
    }
  };

  const orderShortId = customerOrder?.correlationId?.slice(-6) ?? "";
  const orderStatusStyle = getOrderStatusStyle(customerOrder?.orderStatus);
  const paymentStatusStyle = getPaymentStatusStyle(customerOrder?.payment, customerOrder?.orderStatus);
  const orderStatusUpper = customerOrder?.orderStatus ? String(customerOrder.orderStatus).toUpperCase() : "";
  const isTerminalOrderStatus =
    orderStatusUpper === OrderStatusEnum.CLOSED || orderStatusUpper === OrderStatusEnum.VOIDED;
  const paymentStatusRaw = customerOrder?.payment?.paymentStatus;
  const ps = paymentStatusRaw ? String(paymentStatusRaw).toUpperCase() : "";
  const paymentCapturedOrPaid =
    ps === PaymentStatusEnum.PAID || ps === PaymentStatusEnum.CAPTURED;
  const refundEligible =
    ps === PaymentStatusEnum.CAPTURED || ps === PaymentStatusEnum.SETTLED || ps === PaymentStatusEnum.PAID;
  /** Disabled when order is closed/voided or payment is already paid/captured (matches active-order drawer). */
  const updateTipButtonDisabled =
    isUpdatingCustomerOrder || isTerminalOrderStatus || paymentCapturedOrPaid;
  const statusSelectDisabled = isUpdatingCustomerOrder || isTerminalOrderStatus;

  return (
    <div className="p-4 pb-8">
      {/* Order summary header — same as active; optional prev/next */}
      <div className="mb-4 p-3 rounded-lg bg-white border border-[#e5e7eb]">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[14px] font-semibold text-[#111827]">
            Order #{orderShortId}
          </div>
          {(onPrev != null || onNext != null) && (
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={onPrev}
                disabled={!hasPrev}
                className="p-1.5 rounded text-[#6B7280] transition-colors hover:bg-[#f3f4f6] disabled:cursor-not-allowed disabled:opacity-[0.35] disabled:grayscale disabled:hover:bg-transparent"
                aria-label="Previous order"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                </svg>
              </button>
              <button
                type="button"
                onClick={onNext}
                disabled={!hasNext}
                className="p-1.5 rounded text-[#6B7280] transition-colors hover:bg-[#f3f4f6] disabled:cursor-not-allowed disabled:opacity-[0.35] disabled:grayscale disabled:hover:bg-transparent"
                aria-label="Next order"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
          <span
            className={ORDER_BADGE_CLASS}
            style={{
              ...BADGE_PADDING_STYLE,
              backgroundColor: orderStatusStyle.backgroundColor,
              color: orderStatusStyle.color,
            }}
          >
            {orderStatusStyle.label}
          </span>
          <span
            className={PAYMENT_BADGE_CLASS}
            style={{
              ...BADGE_PADDING_STYLE,
              backgroundColor: paymentStatusStyle.backgroundColor,
              color: paymentStatusStyle.color,
            }}
          >
            {paymentStatusStyle.label}
          </span>
        </div>
        <div className="text-[19px] font-bold text-[#111827] mt-2">
          {customerOrder?.totalCents != null
            ? formatCentsToDollarsWithDollarSign(customerOrder.totalCents)
            : "—"}
        </div>
      </div>

      {/* Actions: Download (primary), Print (secondary) */}
      <div className="flex flex-wrap items-center gap-2 pb-4 border-b border-[#e5e7eb]">
        <button
          type="button"
          onClick={downloadReceipt}
          disabled={isDownloading}
          className={orderDrawerPrimaryButtonClass}
        >
          {isDownloading ? "Downloading…" : "Download"}
        </button>
        {onPrint && (
          <>
            <span className="w-px h-5 bg-[#e5e7eb] mx-0.5" aria-hidden />
            <button
              type="button"
              onClick={onPrint}
              className={orderDrawerSecondaryButtonClass}
            >
              Print
            </button>
          </>
        )}
        {refundEligible && (
          <>
            <span className="w-px h-5 bg-[#e5e7eb] mx-0.5" aria-hidden />
            <button
              type="button"
              onClick={() => setRefundModalOpen(true)}
              disabled={refundSubmitting}
              className={orderDrawerSecondaryButtonClass}
            >
              {refundSubmitting ? "Refunding..." : "Refund"}
            </button>
          </>
        )}
      </div>

      {/* Order Details — same layout as active */}
      <h2 className="text-[14px] font-semibold text-[#111827] mt-4 mb-2">Order Details</h2>
      <div className="space-y-2" style={{ marginTop: 4 }}>
        <div className="flex items-center min-h-[24px]">
          <span className="text-[14px] text-[#6B7280] w-[140px] shrink-0">Order Type</span>
          <span className="text-[14px] font-semibold text-[#111827]">
            {customerOrder?.orderType
              ?.replace(/_/g, " ")
              .toLowerCase()
              .replace(/^./, (c: string) => c.toUpperCase()) || "—"}
          </span>
        </div>
        <div className="flex items-center min-h-[24px]">
          <span className="text-[14px] text-[#6B7280] w-[140px] shrink-0">Table Number</span>
          <span className="text-[14px] font-semibold text-[#111827]">{customerOrder?.tableNumber ?? "—"}</span>
        </div>
        {customerOrder?.orderType === OrderTypeEnum.DINE_IN && customerOrder?.partySize != null && (
          <div className="flex items-center min-h-[24px]">
            <span className="text-[14px] text-[#6B7280] w-[140px] shrink-0">Party size</span>
            <span className="text-[14px] font-semibold text-[#111827]">{customerOrder.partySize}</span>
          </div>
        )}
        {(customerOrder?.channelName || customerOrder?.channelOrderNumber) && (
          <div className="flex items-center min-h-[24px]">
            <span className="text-[14px] text-[#6B7280] w-[140px] shrink-0">Channel</span>
            <span className="text-[14px] font-semibold text-[#111827]">
              {[customerOrder.channelName, customerOrder.channelOrderNumber].filter(Boolean).join(" · ") || "—"}
            </span>
          </div>
        )}
        <div className="flex items-center min-h-[32px] min-w-0">
          <span className="text-[14px] text-[#6B7280] w-[140px] shrink-0">Tip</span>
          <div className="flex flex-1 min-w-0 items-center gap-x-2 gap-y-1.5 flex-wrap">
            <div
              className={`inline-flex items-center min-w-0 border border-[#E5E7EB] rounded-md overflow-hidden bg-white focus-within:border-[#d1d5db] h-8 ${ORDER_DRAWER_CONTROL_WIDTH}`}
            >
              <span className="text-[14px] text-[#6B7280] pl-1.5 shrink-0">$</span>
              <input
                id="tip"
                name="tip"
                value={formik.values.tip ?? ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                type="number"
                placeholder="0.00"
                className="text-[13px] font-medium text-[#111827] flex-1 min-w-0 py-1 pr-1.5 text-right tabular-nums border-0 bg-transparent outline-none focus:outline-none focus:ring-0 focus-visible:outline-none shadow-none [-webkit-tap-highlight-color:transparent] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            <button
              type="button"
              onClick={handleUpdateTip}
              disabled={updateTipButtonDisabled}
              className={orderDrawerCompactSecondaryButtonClass}
            >
              Update Tip
            </button>
          </div>
        </div>
        <div className="flex items-center min-h-[32px] min-w-0">
          <span className="text-[14px] text-[#6B7280] w-[140px] shrink-0">Order Status</span>
          <OrderStatusMenuSelect
            stretchToWrapper
            className={ORDER_DRAWER_CONTROL_WIDTH}
            value={customerOrder?.orderStatus}
            onChange={handleStatusChange}
            disabled={statusSelectDisabled}
          />
        </div>
        {orderStatusUpper === OrderStatusEnum.VOIDED && (
          <div className="mt-3 rounded-lg border border-[#fecdd3] bg-[#fff1f2] px-3 py-2.5">
            <div className="text-[12px] font-semibold uppercase tracking-wide text-[#9f1239] mb-2">
              Void information
            </div>
            <div className="space-y-2">
              <div className="flex items-start min-h-[22px]">
                <span className="text-[14px] text-[#6B7280] w-[140px] shrink-0">Voided by</span>
                <span className="text-[14px] font-semibold text-[#111827]">
                  {customerOrder?.voidedBy?.trim() || "—"}
                </span>
              </div>
              <div className="flex items-start min-h-[22px]">
                <span className="text-[14px] text-[#6B7280] w-[140px] shrink-0">Reason</span>
                <span className="text-[14px] font-semibold text-[#111827]">
                  {formatOrderVoidReasonCode(customerOrder?.voidReasonCode ?? undefined)}
                </span>
              </div>
              <div className="flex items-start min-h-[22px]">
                <span className="text-[14px] text-[#6B7280] w-[140px] shrink-0 align-top pt-0.5">Note</span>
                <span className="text-[14px] font-medium text-[#111827] whitespace-pre-wrap break-words min-w-0">
                  {customerOrder?.voidReasonNote?.trim() || "—"}
                </span>
              </div>
              <div className="flex items-start min-h-[22px]">
                <span className="text-[14px] text-[#6B7280] w-[140px] shrink-0">Voided at</span>
                <span className="text-[14px] font-semibold text-[#111827] tabular-nums">
                  {formatVoidedAt(customerOrder?.voidedAt ?? undefined)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="border-b border-[#e5e7eb] my-4" />

      {/* Items + summary — same as active */}
      <h2 className="text-[14px] font-semibold text-[#111827] mb-2">Items</h2>
      <div className="rounded-lg border border-[#e5e7eb] bg-white overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-[#6B7280] border-b border-[#e5e7eb]">
              <th className="text-left font-medium py-1.5 px-2">Name</th>
              <th className="text-center font-medium py-1.5 px-2 w-14">Qty</th>
              <th className="text-right font-medium py-1.5 px-2 w-16">Price</th>
              <th className="text-right font-medium py-1.5 px-2 w-20">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {(customerOrder?.items ?? []).map((item: { correlationId?: string; englishName?: string; quantity?: number; unitPriceCents?: number | null; priceCents?: number | null; lineSubtotalCents?: number | null }) => {
              const unitCents = item.unitPriceCents ?? item.priceCents;
              const subtotalCents =
                item.lineSubtotalCents != null
                  ? item.lineSubtotalCents
                  : unitCents != null && item.quantity != null
                    ? Number(unitCents) * item.quantity
                    : null;
              return (
              <tr key={item?.correlationId} className="border-b border-[#f3f4f6]">
                <td className="text-[#111827] py-1 px-2">{item?.englishName ?? "—"}</td>
                <td className="text-center text-[#111827] py-1 px-2">{item?.quantity ?? 0}</td>
                <td className="text-right text-[#111827] tabular-nums py-1 px-2">
                  {unitCents != null ? formatCentsToDollarsWithDollarSign(Number(unitCents)) : "—"}
                </td>
                <td className="text-right font-medium text-[#111827] tabular-nums py-1 px-2">
                  {subtotalCents != null ? formatCentsToDollarsWithDollarSign(subtotalCents) : "—"}
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
        <div className="border-t border-[#e5e7eb] text-[13px]">
          <div className="flex justify-between px-2 py-1.5 bg-[#f9fafb]">
            <span className="text-[#6B7280]">Subtotal</span>
            <span className="font-medium text-[#111827] tabular-nums">
              {customerOrder?.subtotalCents != null ? formatCentsToDollarsWithDollarSign(customerOrder.subtotalCents) : "—"}
            </span>
          </div>
          {(customerOrder.discounts ?? []).filter(Boolean).map((d: OrderDiscount, i: number) => (
            <div
              key={d.correlationId ?? `order-discount-${i}`}
              className="flex justify-between px-2 py-1.5 bg-[#f9fafb]"
            >
              <span className="text-[#6B7280]">
                {formatOrderDiscountRuleDisplay(d, {
                  appliedPromotionCode: customerOrder?.appliedPromotionCode,
                  promotionSnapshotJson: customerOrder?.promotionSnapshotJson,
                })}
              </span>
              <span className="font-medium text-[#111827] tabular-nums">{formatOrderDiscountValueDisplay(d)}</span>
            </div>
          ))}
          {(!(customerOrder.discounts ?? []).length &&
            customerOrder.orderTotalDiscountCents != null &&
            customerOrder.orderTotalDiscountCents !== 0) && (
            <div className="flex justify-between px-2 py-1.5 bg-[#f9fafb]">
              <span className="text-[#6B7280]">
                {formatOrderDiscountRuleDisplay(
                  {
                    discountCents: customerOrder.orderTotalDiscountCents,
                    discountReference: customerOrder.appliedPromotionCode ?? undefined,
                  },
                  {
                    appliedPromotionCode: customerOrder?.appliedPromotionCode,
                    promotionSnapshotJson: customerOrder?.promotionSnapshotJson,
                  }
                )}
              </span>
              <span className="font-medium text-[#111827] tabular-nums">
                -{formatCentsToDollarsWithDollarSign(Math.abs(Number(customerOrder.orderTotalDiscountCents)))}
              </span>
            </div>
          )}
          <div className="flex justify-between px-2 py-1.5 bg-[#f9fafb]">
            <span className="text-[#6B7280]">Tax</span>
            <span className="font-medium text-[#111827] tabular-nums">
              {customerOrder?.totalTaxCents != null ? formatCentsToDollarsWithDollarSign(customerOrder.totalTaxCents) : "—"}
            </span>
          </div>
          <div className="flex justify-between px-2 py-1.5 bg-[#f9fafb]">
            <span className="text-[#6B7280]">Tip</span>
            <span className="font-medium text-[#111827] tabular-nums">
              {customerOrder?.tipCents != null ? formatCentsToDollarsWithDollarSign(customerOrder.tipCents) : "—"}
            </span>
          </div>
          {customerOrder?.cardProcessingFeeCents != null && customerOrder.cardProcessingFeeCents > 0 && (
            <div className="flex justify-between px-2 py-1.5 bg-[#f9fafb]">
              <span className="text-[#6B7280]">Card processing fee</span>
              <span className="font-medium text-[#111827] tabular-nums">
                {formatCentsToDollarsWithDollarSign(customerOrder.cardProcessingFeeCents)}
              </span>
            </div>
          )}
          <div className="flex justify-between px-2 py-2 font-bold text-[#111827] bg-[#f3f4f6]">
            <span>Total</span>
            <span className="tabular-nums">
              {customerOrder?.totalCents != null ? formatCentsToDollarsWithDollarSign(customerOrder.totalCents) : "—"}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <CustomerDetails customerOrder={customerOrder} compact />
      </div>
      {customerOrder?.payment && (
        <div className="mt-4">
          <PaymentDetails payment={customerOrder.payment} compact />
        </div>
      )}
      <div className="mt-4 rounded-lg border border-[#e5e7eb] bg-white p-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-[#111827]">Signature</div>
            <div className="text-xs text-[#6b7280]">
              {customerOrder?.signatureCapturedAt
                ? new Date(customerOrder.signatureCapturedAt).toLocaleString()
                : "No signature saved"}
            </div>
          </div>
          <button
            type="button"
            disabled={!customerOrder?.signatureBucketKeyName || signatureLoading}
            onClick={viewSignature}
            className="rounded-md bg-[#FD7657] px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {signatureLoading ? "Loading..." : "View"}
          </button>
        </div>
      </div>

      {signatureUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSignatureUrl(null)}>
          <div className="max-w-3xl rounded-xl bg-white p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#111827]">Customer Signature</h3>
              <button type="button" className="text-sm font-semibold text-[#6b7280]" onClick={() => setSignatureUrl(null)}>
                Close
              </button>
            </div>
            <img src={signatureUrl} alt="Customer signature" className="max-h-[70vh] w-full object-contain" />
          </div>
        </div>
      )}

      <ConfirmOrderStatusChangeModal
        open={pendingStatusChange != null}
        pendingStatus={pendingStatusChange}
        onConfirm={handleConfirmStatusChange}
        onCancel={handleCancelStatusChange}
        isLoading={statusConfirmLoading}
      />
      <VoidOrderModal
        open={voidModalOpen}
        voidedByDisplay={resolveVoidedByDisplay(user)}
        onConfirm={handleConfirmVoid}
        onCancel={handleCancelVoid}
        isLoading={voidSubmitting || isUpdatingCustomerOrder}
      />
      <ConfirmActionModal
        open={refundModalOpen}
        title="Refund order"
        message={`Submit a refund for order #${orderShortId}? The payment status will update after the processor confirms the refund.`}
        confirmLabel="Refund"
        loadingLabel="Refunding..."
        onConfirm={refundOrderPayment}
        onCancel={handleCancelRefund}
        isLoading={refundSubmitting}
      />
    </div>
  );
}
